import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string | string[];
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: '¿Qué nos diferencia?',
      answer: 'En nuestra escuela infantil, cada mirada, cada logro, cada lágrima y cada risa cuenta.\n\nNo llenamos horarios: llenamos corazones.\n\nAcompañamos a los niños con ternura, con tiempo, con presencia real.\n\nPorque aquí no se trata solo de enseñar, sino de cuidar lo que más quieres con el alma, como si fuera nuestro.\n\nY de crecer juntos: familias, educadoras y peques, mano a mano, paso a paso.'
    },
    {
      question: '¿Qué ofrecemos?',
      answer: [
        'Vínculo afectivo real: Nos implicamos de verdad. Cada educadora conoce a cada peque y su mundo.',
        'Educación con sentido: Actividades pensadas para estimular su desarrollo emocional, social y cognitivo.',
        'Ambiente familiar y cercano: Aquí las familias cuentan. Caminamos juntos, con confianza y escucha.',
        'Espacios cálidos y seguros: Todo está diseñado para que se sientan como en casa, pero con oportunidades infinitas para explorar.',
        'Rutinas que cuidan: El descanso, la comida, el juego, el movimiento… todo se hace con amor, con calma, con respeto.',
        'Profesionales con vocación: No es un trabajo. Es una forma de estar en el mundo: con ternura, compromiso y alegría.'
      ]
    },
    {
      question: '¿Cómo comenzó todo?',
      answer: 'Con un sueño… y con mucho corazón.\n\nEsta escuela nació de una idea sencilla pero poderosa: crear un lugar donde la infancia se respete, se escuche y se viva con alegría.\n\nQueríamos un espacio donde los niños no fueran "dejados", sino acogidos, acompañados, valorados.\n\nUn lugar donde las familias sintieran confianza, y donde cada pequeño gesto construyera un gran vínculo.\n\nY así, poquito a poco, con ilusión, esfuerzo y muchísimo amor, fuimos dando forma a esta pequeña gran escuela.\n\nUna escuela que no nació para ser la más grande, sino la más cercana.\n\nLa que deja huella en los corazones.'
    },
    {
      question: '¿Por qué elegirnos?',
      answer: 'Porque aquí los niños no solo crecen. Se sienten amados. Elegir escuela infantil no es fácil. Lo sabemos.\n\nPor eso queremos que nos elijas con el corazón tranquilo, sabiendo que aquí tu hijo o hija será cuidado, respetado y escuchado cada día.\n\nPorque aquí no hay prisas, hay presencia.\n\nNo hay moldes, hay mirada individual.\n\nNo hay una infancia ideal, hay la infancia real de cada peque, con sus ritmos, sus emociones y su magia.\n\nElegirnos es elegir un lugar donde tu familia importa. Donde educamos desde el vínculo, la alegría y el cariño.\n\nY donde, cuando te vas, sabes que dejas lo más valioso del mundo… en las mejores manos.'
    },
    {
      question: '¿Cómo ayudamos?',
      answer: 'Estando. Siempre.\n\nAyudamos dando tranquilidad a las familias que necesitan conciliar, sabiendo que aquí sus hijos están cuidados con amor, con respeto y con tiempo de calidad.\n\nSomos ese abrazo cuando no puedes estar. Esa mirada atenta que acompaña sus primeros pasos, sus primeras palabras, sus pequeños grandes logros.\n\nAquí, el trabajo y la maternidad/paternidad no tienen por qué ser enemigos.\n\nAquí entendemos lo que implica dejar a tu peque por las mañanas… y por eso, cada gesto nuestro está lleno de cariño.\n\nAyudamos estando presentes, creando vínculos sólidos, y haciendo equipo con las familias.\n\nPorque no solo educamos a niños: cuidamos a quienes los cuidan.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Preguntas Frecuentes</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Resolvemos tus dudas sobre nuestra escuela infantil
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-[#F5F1E8] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-md"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-[#FF7F6E] transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6">
                  {Array.isArray(faq.answer) ? (
                    <ul className="space-y-3">
                      {faq.answer.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-[#FF7F6E] mr-3 mt-1 flex-shrink-0">•</span>
                          <span className="text-gray-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
